/**
 * Sprint D — Modale historique des versions DPGF.
 *
 * Liste les versions, possibilité de restaurer (archi only).
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, ArrowCounterClockwise, ClockCounterClockwise } from '@phosphor-icons/react';
import { Button } from '../ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ORIGINE_LABELS = {
  auto_save: 'Sauvegarde auto',
  auto_avant_restauration: 'Avant restauration',
  manual: 'Sauvegarde manuelle',
};

export default function DPGFVersionsModal({ open, onClose, chantierId, onRestored, canRestore = false }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoringId, setRestoringId] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    axios.get(`${API_URL}/api/chantiers/${chantierId}/dpgf/versions`, { withCredentials: true })
      .then((r) => setVersions(Array.isArray(r.data) ? r.data : []))
      .catch(() => toast.error('Impossible de charger les versions'))
      .finally(() => setLoading(false));
  }, [open, chantierId]);

  const handleRestore = async (versionId) => {
    if (!window.confirm('Restaurer cette version ? La version actuelle sera sauvegardée dans l\'historique.')) return;
    setRestoringId(versionId);
    try {
      await axios.post(
        `${API_URL}/api/chantiers/${chantierId}/dpgf/versions/${versionId}/restaurer`,
        {},
        { withCredentials: true },
      );
      toast.success('Version restaurée');
      onRestored?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur restauration');
    } finally {
      setRestoringId(null);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="dpgf-versions-modal"
    >
      <div
        className="bg-white shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b border-[#E0E0DB] flex items-center justify-between bg-[#FAFAF8]">
          <div className="flex items-center gap-2">
            <ClockCounterClockwise size={18} className="text-[#1E3A5F]" />
            <h3 className="text-base font-bold text-[#2C2C2C]">Historique des versions DPGF</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-white rounded-none"
            data-testid="dpgf-versions-close"
            aria-label="Fermer"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-6 text-center text-sm text-[#9A9A9A]">Chargement…</div>
          ) : versions.length === 0 ? (
            <div className="p-8 text-center text-sm text-[#9A9A9A] italic">
              Aucune version historique pour ce DPGF.
            </div>
          ) : (
            <ul className="divide-y divide-[#E0E0DB]">
              {versions.map((v) => {
                const lots = v.lots || [];
                const totalPostes = lots.reduce((acc, l) => acc + ((l.postes || []).length), 0);
                const date = v.saved_at ? new Date(v.saved_at) : null;
                const dateStr = date ? date.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—';
                return (
                  <li
                    key={v.id}
                    className="p-3 flex items-center gap-3 hover:bg-[#FAFAF8]"
                    data-testid={`dpgf-version-${v.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-[#2C2C2C]">{dateStr}</div>
                      <div className="text-xs text-[#6B6B6B] mt-0.5">
                        {lots.length} lot{lots.length > 1 ? 's' : ''} · {totalPostes} poste{totalPostes > 1 ? 's' : ''}
                      </div>
                      {v.origine && v.origine !== 'auto_save' && (
                        <div className="text-[10px] uppercase tracking-wider text-[#C4773B] font-bold mt-1">
                          {ORIGINE_LABELS[v.origine] || v.origine}
                        </div>
                      )}
                    </div>
                    {canRestore && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-none gap-1 text-xs border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-white"
                        onClick={() => handleRestore(v.id)}
                        disabled={restoringId === v.id}
                        data-testid={`dpgf-version-restore-${v.id}`}
                      >
                        <ArrowCounterClockwise size={12} />
                        {restoringId === v.id ? 'Restauration…' : 'Restaurer'}
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
